import React, { useState, useEffect, useRef } from 'react';
import styles from './TopB.module.scss';
import { motion, useScroll, useTransform, useSpring, AnimatePresence } from 'framer-motion';

const ThinkingDots: React.FC = () => (
  <div className={styles.thinking}>
    <span></span>
    <span></span>
    <span></span>
  </div>
);

const Section: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <motion.section
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.8, ease: "easeOut" }}
    className={className}
  >
    {children}
  </motion.section>
);

const Top: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "center end"]
  });

  // Smooth scroll progress for animations
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  // Calculate chat steps based on scroll
  const [chatStep, setChatStep] = useState(0);

  useEffect(() => {
    return scrollYProgress.onChange((latest) => {
      if (latest < 0.1) setChatStep(0);
      else if (latest < 0.3) setChatStep(1);
      else if (latest < 0.5) setChatStep(2);
      else if (latest < 0.7) setChatStep(3);
      else if (latest < 0.9) setChatStep(5);
      else setChatStep(6);
    });
  }, [scrollYProgress]);

  // Derived mockup states
  const mockupScale = useTransform(smoothProgress, [0, 1], [0.95, 1.05]);
  const mockupRotate = useTransform(smoothProgress, [0, 1], [5, 2]);

  return (
    <div className={styles.containerWrap} ref={containerRef}>
      <header className={styles.header}>
        <div className={styles.logoArea}>
          <img src="/images/ai-bex-logo.png" alt="Aibex Logo" className={styles.logo} />
          <span className={styles.logoText}>マーケティング特化AIエージェント</span>
        </div>
        <div className={styles.navButtons}>
          <button className={styles.loginBtn}>ログイン</button>
          <button className={styles.demoBtnHeader}>無料デモ申込</button>
        </div>
      </header>

      <main className={styles.heroCentered}>
        <div className={styles.heroText}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className={styles.performanceBadge}
          >
            年間4億円以上の運用実績
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className={styles.tagline}
          >
            広告成果を最大化する<br />
            AIエージェント Aibex
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className={styles.subCopy}
          >
            AIとの対話が、分析から改善提案までをナビゲート。<br />
            あなたは「決める」だけに集中。
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className={styles.benefitGrid}
          >
            <div className={styles.benefitItem}>
              <div className={styles.benefitIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>
              </div>
              <div className={styles.benefitText}>
                <span className={styles.benefitLabel}>AI自動診断</span>
                <span className={styles.benefitValue}>分析時間を90%削減</span>
              </div>
            </div>
            <div className={styles.benefitItem}>
              <div className={styles.benefitIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline><polyline points="16 7 22 7 22 13"></polyline></svg>
              </div>
              <div className={styles.benefitText}>
                <span className={styles.benefitLabel}>運用最適化</span>
                <span className={styles.benefitValue}>CPAを平均25%改善</span>
              </div>
            </div>
            <div className={styles.benefitItem}>
              <div className={styles.benefitIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
              </div>
              <div className={styles.benefitText}>
                <span className={styles.benefitLabel}>レポート生成</span>
                <span className={styles.benefitValue}>資料作成時間をゼロへ</span>
              </div>
            </div>
          </motion.div>

          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className={styles.primaryCta}
          >
            <div className={styles.ctaContent}>
              <span className={styles.mainLabel}>無料デモ申込</span>
              <span className={styles.subLabel}>14日間の無料体験で広告成果を最大に。</span>
            </div>
            <span className={styles.ctaIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
            </span>
          </motion.button>
        </div>

        <div className={styles.visualContainer}>
          <motion.div
            className={styles.pcMockupFrame}
            style={{ scale: mockupScale, rotateX: mockupRotate }}
          >
            <div className={styles.mockupContent}>
              <img src="/images/pc_moc.png" alt="Aibex PC Mockup" className={styles.pcImage} />

              {/* Dynamic Mockup Overlays */}
              <AnimatePresence>
                {chatStep >= 6 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className={styles.mockupValueOverlay}
                  >
                    <div className={styles.metricCard}>
                      <span className={styles.metricLabel}>Cost Efficiency</span>
                      <span className={styles.metricValue}>+24.8%</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <motion.div
                className={styles.mockupHighlightOverlay}
                animate={chatStep >= 2 ? { opacity: 0.4 } : { opacity: 0 }}
              />
            </div>
          </motion.div>

          <div className={styles.chatOverlay}>
            <AnimatePresence>
              {chatStep >= 1 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.5, y: 20, x: -20 }}
                  animate={{ opacity: 1, scale: 1, y: 0, x: 0 }}
                  exit={{ opacity: 0, scale: 0.5, y: 20 }}
                  transition={{ type: "spring", stiffness: 200, damping: 15 }}
                  className={`${styles.chatBubble} ${styles.messageAi} ${chatStep === 1 ? styles.thinkingBubble : styles.visibleBubble
                    }`}
                >
                  <div className={styles.aiIcon}>AI</div>
                  <div className={styles.bubbleContent}>
                    {chatStep === 1 ? (
                      <ThinkingDots />
                    ) : (
                      <>
                        Google広告のコストが悪化しています。<br />
                        ターゲティングの見直しをご提案します。
                      </>
                    )}
                  </div>
                </motion.div>
              )}

              {chatStep >= 3 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.5, y: 20, x: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0, x: 0 }}
                  exit={{ opacity: 0, scale: 0.5, y: 20 }}
                  transition={{ type: "spring", stiffness: 200, damping: 15 }}
                  className={`${styles.chatBubble} ${styles.messageUser} ${chatStep === 3 ? styles.thinkingBubble : styles.visibleBubble
                    }`}
                >
                  <div className={styles.bubbleContent}>
                    {chatStep === 3 ? <ThinkingDots /> : <>お願いします。見直しを開始してください。</>}
                  </div>
                </motion.div>
              )}

              {chatStep >= 5 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.5, y: 20, x: -20 }}
                  animate={{ opacity: 1, scale: 1, y: 0, x: 0 }}
                  exit={{ opacity: 0, scale: 0.5, y: 20 }}
                  transition={{ type: "spring", stiffness: 200, damping: 15 }}
                  className={`${styles.chatBubble} ${styles.messageAi} ${chatStep === 5 ? styles.thinkingBubble : styles.visibleBubble
                    }`}
                >
                  <div className={styles.bubbleContent}>
                    {chatStep === 5 ? (
                      <ThinkingDots />
                    ) : (
                      <>分析完了！CPAが大幅に改善されました。</>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Logo Slider Section */}
        <div className={styles.logoSliderContainer}>
          <div className={styles.logoSliderTrack}>
            <div className={styles.logoSliderRowRight}>
              {[...Array(20)].map((_, i) => (
                <div key={`logo-u-${i}`} className={styles.logoPlaceholder}>LOGO {i + 1}</div>
              ))}
            </div>
            <div className={styles.logoSliderRowLeft}>
              {[...Array(20)].map((_, i) => (
                <div key={`logo-d-${i}`} className={styles.logoPlaceholder}>LOGO {i + 21}</div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* 1. 課題提示セクション */}
      <Section className={styles.sectionIssues}>
        <h2 className={styles.sectionTitle}>成果を伸ばせない理由は、<br />運用の“手間と複雑さ”にあります。</h2>
        <div className={styles.issueGrid}>
          <div className={styles.issueCard}>
            <h3>何からすべきか不明</h3>
            <p>データはあっても、どこを改善すれば成果に繋がるのかの判断が困難です。</p>
          </div>
          <div className={styles.issueCard}>
            <h3>分析の時間が足りない</h3>
            <p>日々の運用に追われ、深い分析や戦略立案に時間を割くことができません。</p>
          </div>
          <div className={styles.issueCard}>
            <h3>根拠に自信がない</h3>
            <p>経験や勘に頼った運用になり、確かなデータに基づいた意思決定ができません。</p>
          </div>
        </div>
      </Section>

      {/* 2. 解決策（Aibexとは） */}
      <Section className={styles.sectionSolution}>
        <div className={styles.solutionContent}>
          <h2 className={styles.sectionTitle}>その悩み、AIエージェント『Aibex』が<br />すべて解決します。</h2>
          <p className={styles.sectionSubText}>
            GA4や広告データを多角的に分析。これまで数時間かかっていた分析・提案を自動化します。
          </p>
        </div>
      </Section>

      {/* 3. Aibexにできること（3つのインテリジェンス） */}
      <Section className={styles.sectionFeatures}>
        <div className={styles.featureItem}>
          <div className={styles.featureText}>
            <span className={styles.featureLabel}>Diagnosis Mode</span>
            <h3>診断モード</h3>
            <p>AIが主要指標を診断し、無駄なコストや改善余地を発見。</p>
            <p className={styles.featureDetail}>
              CVRに貢献しているページや広告クリエイティブ、逆にユーザーの離脱原因となっているボトルネックを自動で特定。マーケティング初心者でも見るべきポイントがサクッと分かります。
            </p>
          </div>
          <div className={styles.featureImage}>
            <img src="/images/moc_diagnosis.png" alt="Diagnosis Mode" />
          </div>
        </div>

        <div className={`${styles.featureItem} ${styles.reverse}`}>
          <div className={styles.featureText}>
            <span className={styles.featureLabel}>Suggestion Mode</span>
            <h3>提案モード</h3>
            <p>聞く・頼むをチャットで完結。広告運用を即アクション。</p>
            <p className={styles.featureDetail}>
              「このバナーは成果が出ていますか？」「先月と比較してCVRが落ちた原因は？」といった自然な言葉での質問に対し、AIがデータに基づいて分かりやすく回答。広告出稿の依頼までチャットで完結。
            </p>
          </div>
          <div className={styles.featureImage}>
            <img src="/images/moc_suggestion.png" alt="Suggestion Mode" />
          </div>
        </div>

        <div className={styles.featureItem}>
          <div className={styles.featureText}>
            <span className={styles.featureLabel}>Review Mode</span>
            <h3>振り返りモード</h3>
            <p>施策とその検証結果をAIが見逃さずチェック。</p>
            <p className={styles.featureDetail}>
              施策を記録し、定期的にその施策による影響をウォッチします。広告データの重要な変化・改善をAIが能動的に検知し、漏れなくPDCAをまわすことで確実に施策を前進させます。
            </p>
          </div>
          <div className={styles.featureImage}>
            <div className={styles.featureMockPlaceholder}>
              <div className={styles.pdcaCircle}>
                <span>PLAN</span>
                <span>DO</span>
                <span>CHECK</span>
                <span>ACTION</span>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* 4/5. 強力なオファー & 料金プラン */}
      <Section className={styles.sectionPricing}>
        <div className={styles.pricingHeader}>
          <span className={styles.pricingBadge}>料金プラン</span>
          <h2 className={styles.pricingTitle}>経験豊富なAIマーケターを、<br />正社員の1/6の金額で採用。</h2>
        </div>

        <div className={styles.offerBox}>
          <p className={styles.offerNotice}>現在テスト運用開始中！3ヶ月間<span className={styles.highlight}>無料</span>でお試しいただけます。</p>
          <div className={styles.pricingGrid}>
            <div className={styles.priceItem}>
              <span className={styles.priceLabel}>初期費用</span>
              <div className={styles.priceValue}>
                <span className={styles.oldPrice}>¥300,000</span>
                <span className={styles.newPrice}>¥0</span>
                <span className={styles.priceUnit}>/ 初回のみ</span>
              </div>
            </div>
            <div className={styles.priceItem}>
              <span className={styles.priceLabel}>月額利用料</span>
              <div className={styles.priceValue}>
                <span className={styles.oldPrice}>¥39,800</span>
                <span className={styles.newPrice}>¥0</span>
                <span className={styles.priceUnit}>/ 月 + 広告運用費の5%</span>
              </div>
            </div>
          </div>
          <div className={styles.offerCta}>
            <button className={styles.offerBtn}>無料で今すぐ試してみる</button>
          </div>
        </div>
      </Section>

      {/* 6. よくある質問 */}
      <Section className={styles.sectionFaq}>
        <h2 className={styles.sectionTitle}>よくある質問</h2>
        <div className={styles.faqList}>
          <div className={styles.faqItem}>
            <div className={styles.faqQuestion}>Q. ITやマーケティングの専門知識がなくても使えますか？</div>
            <div className={styles.faqAnswer}>A. はい、もちろんです。AIが自然な言葉での質問に応答し、専門用語も噛み砕いて説明するため、初心者の方でも安心して運用いただけます。</div>
          </div>
          <div className={styles.faqItem}>
            <div className={styles.faqQuestion}>Q. 対応している広告媒体を教えてください。</div>
            <div className={styles.faqAnswer}>A. 現在、Google広告、Meta広告（Facebook/Instagram）、Yahoo!広告に対応しています。今後さらに順次拡大予定です。</div>
          </div>
          <div className={styles.faqItem}>
            <div className={styles.faqQuestion}>Q. トライアル後に必ず契約する必要がありますか？</div>
            <div className={styles.faqAnswer}>A. いいえ、トライアル期間中にご判断いただけます。自動的に有料プランへ移行することはないため、まずはお気軽にお試しください。</div>
          </div>
        </div>
      </Section>

      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <img src="/images/ai-bex-logo.png" alt="Aibex Logo" className={styles.footerLogo} />
          <p className={styles.copyright}>&copy; 2025 Aibex Inc. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Top;
